/**
 * Zoom actions utility - Handles zoom operations for different modes
 */

import type { ZoomMode } from "../types/user-types.js";

export interface ZoomActionConfig {
  readonly viewport: {
    readonly width: number;
    readonly height: number;
  };
  readonly content?: {
    readonly width: number;
    readonly height: number;
  };
  readonly selectedElement?: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  };
}

export interface ZoomResult {
  readonly value: number;
  readonly success: boolean;
  readonly error?: string;
}

/**
 * Calculate zoom value for a given zoom mode
 * @param mode - The zoom mode to apply
 * @param config - Configuration with viewport and content dimensions
 * @returns Zoom result with calculated value
 */
export const calculateZoomForMode = (
  mode: ZoomMode,
  config: ZoomActionConfig
): ZoomResult => {
  const { viewport, content, selectedElement } = config;

  try {
    switch (mode) {
      case "zoom_width": {
        if (!content) {
          return {
            value: 1.0,
            success: false,
            error: "Content dimensions required for fit width"
          };
        }
        
        const zoomValue = viewport.width / content.width;
        return {
          value: Math.max(0.1, Math.min(10, zoomValue)),
          success: true
        };
      }

      case "zoom_height": {
        if (!content) {
          return {
            value: 1.0,
            success: false,
            error: "Content dimensions required for fit height"
          };
        }
        
        const zoomValue = viewport.height / content.height;
        return {
          value: Math.max(0.1, Math.min(10, zoomValue)),
          success: true
        };
      }

      case "zoom_all": {
        if (!content) {
          return {
            value: 1.0,
            success: false,
            error: "Content dimensions required for fit all"
          };
        }
        
        const scaleX = viewport.width / content.width;
        const scaleY = viewport.height / content.height;
        const zoomValue = Math.min(scaleX, scaleY);
        
        return {
          value: Math.max(0.1, Math.min(10, zoomValue)),
          success: true
        };
      }

      case "zoom_element": {
        if (!selectedElement) {
          return {
            value: 1.0,
            success: false,
            error: "Selected element required for fit element"
          };
        }
        
        const scaleX = viewport.width / selectedElement.width;
        const scaleY = viewport.height / selectedElement.height;
        const zoomValue = Math.min(scaleX, scaleY) * 0.9; // Add 10% padding
        
        return {
          value: Math.max(0.1, Math.min(10, zoomValue)),
          success: true
        };
      }

      case "zoom_1_1": {
        return {
          value: 1.0,
          success: true
        };
      }

      case "custom": {
        // For custom mode, return current value as-is
        return {
          value: 1.0,
          success: true
        };
      }

      default: {
        return {
          value: 1.0,
          success: false,
          error: `Unknown zoom mode: ${mode}`
        };
      }
    }
  } catch (error) {
    return {
      value: 1.0,
      success: false,
      error: `Failed to calculate zoom: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

/**
 * Get the icon name for a zoom mode
 * @param mode - The zoom mode
 * @returns Icon name string
 */
export const getZoomModeIcon = (mode: ZoomMode): string => {
  switch (mode) {
    case "zoom_width":
      return "zoom_width";
    case "zoom_height":
      return "zoom_height";
    case "zoom_all":
      return "zoom_all";
    case "zoom_element":
      return "zoom_element";
    case "zoom_1_1":
      return "zoom_1_1";
    case "custom":
      return "zoom_all"; // Default icon for custom
    default:
      return "zoom_all";
  }
};

/**
 * Get the display label for a zoom mode
 * @param mode - The zoom mode
 * @returns Human-readable label
 */
export const getZoomModeLabel = (mode: ZoomMode): string => {
  switch (mode) {
    case "zoom_width":
      return "Fit Width";
    case "zoom_height":
      return "Fit Height";
    case "zoom_all":
      return "Fit All";
    case "zoom_element":
      return "Fit Selection";
    case "zoom_1_1":
      return "Actual Size";
    case "custom":
      return "Custom";
    default:
      return "Unknown";
  }
};
